import { useEffect, useRef } from "react";
import { useSetRecoilState } from "recoil";

import {
  useCustomQueryParams,
  useDynamicFormsUtils,
  usePageContext,
} from "@ryde/redux";
import {
  Log,
  tableFiltersSchemaAtomFamily,
  type IFilterSchemaObject,
} from "@ryde/shared";

/**
 * Applies inbound deep-link filters (`?cardFilters=`) to this grid.
 *
 * A dashboard card links here with the filters it was counting, but it can't
 * name them the way the grid does: grid filters are keyed by numeric control id,
 * and the linking page has no schema for this entity loaded. So the link carries
 * CONTROL NAMES (`customFields.name`, what `getCustomControlIdByFieldName`
 * matches — not the grid field name, which can differ) and this hook — which does
 * run inside the target entity's
 * context — resolves them to control ids.
 *
 * Two deliberate properties, both from the deep-link contract:
 * - **Exclusive.** The resolved set REPLACES whatever the normal init produced,
 *   rather than merging over it like `?filters=` does. Otherwise a sticky filter
 *   the user left on this grid could hide rows the card had counted.
 * - **Single navigation.** The param is consumed and cleared, and nothing is
 *   persisted to the local store or the saved view, so a refresh or a later
 *   visit returns the user to their own filters untouched.
 *
 * Runs only once the caller's grid has finished its own filter init
 * (`isReady`), so it overrides the initial filters instead of racing them.
 */
export const useCardFiltersFromQs = ({
  isReady,
}: {
  isReady: boolean;
}): void => {
  const { entity } = usePageContext();
  const { cardFiltersFromQs, resetCardFiltersQs } = useCustomQueryParams();
  const { getCustomControlIdByFieldName } = useDynamicFormsUtils();
  const setTableFilters = useSetRecoilState(
    tableFiltersSchemaAtomFamily(entity),
  );
  const isAppliedRef = useRef(false);

  useEffect(() => {
    if (!cardFiltersFromQs) {
      isAppliedRef.current = false;
      return;
    }

    if (isAppliedRef.current) {
      return;
    }

    if (!isReady) {
      return;
    }

    const resolved: IFilterSchemaObject = {};
    const unresolved: string[] = [];

    Object.entries(cardFiltersFromQs).forEach(([fieldName, value]) => {
      const controlId = getCustomControlIdByFieldName(fieldName);

      if (controlId === undefined) {
        unresolved.push(fieldName);
        return;
      }

      // `IFilterSchemaObject` doesn't admit booleans, but the grid's own yes/no
      // multiselect stores them and casts the same way (see
      // `use-filter-multiselect-bound`), so the store really does hold them.
      resolved[controlId] = value as IFilterSchemaObject[number];
    });

    // Applying a partial set would silently show MORE rows than the card counted,
    // which is worse than not filtering at all — the user would trust a wrong
    // number. Leave the grid exactly as the user had it and say why.
    if (unresolved.length) {
      Log.warn("Deep-link filters not applied, fields not found on entity", {
        entity,
        unresolved,
      });
      isAppliedRef.current = true;
      resetCardFiltersQs();
      return;
    }

    isAppliedRef.current = true;

    // Nothing to apply — don't hand the grid an empty set, which would clear the
    // filters the user already had rather than leave them alone.
    if (!Object.keys(resolved).length) {
      resetCardFiltersQs();
      return;
    }

    setTableFilters(resolved);
    resetCardFiltersQs();
  }, [
    cardFiltersFromQs,
    entity,
    isReady,
    getCustomControlIdByFieldName,
    resetCardFiltersQs,
    setTableFilters,
  ]);
};
